'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import {
    ArrowLeftRight,
    Award,
    ChartCandlestick,
    ChartLine,
    Coins,
    GraduationCap,
    Layers,
    Newspaper,
    Trophy,
    type LucideIcon,
} from 'lucide-react';
import { SpriteIcon } from '@/components/ui/SvgSprite';
import { LayoutProps } from '@/types/generalSettings';

// Column icon from its title (menu titles come from WordPress, so they are matched by keyword)
const ICONS: [RegExp, LucideIcon][] = [
    [/انتخاب|بهترین\s*ها/, Award],
    [/آموزش|دوره/, GraduationCap],
    [/قیمت/, ChartLine],
    [/صراف/, ArrowLeftRight],
    [/بروکر/, ChartCandlestick],
    [/مقال|اخبار|بلاگ/, Newspaper],
    [/پراپ/, Trophy],
    [/طلا|سکه/, Coins],
];
const iconFor = (label: string) => ICONS.find(([pattern]) => pattern.test(label))?.[1] || Layers;

// WordPress titles carry emoji (🥇, 🎬); the icon replaces them
const cleanLabel = (label: string) => label.replace(/[\p{Extended_Pictographic}️‍]/gu, '').trim();

/**
 * One link column. Phones: an accordion (the whole row toggles), collapsed except the first one, so the
 * footer stays short but its most important links are visible.
 * Desktop: always open, the title links to its page. Links are always in the HTML, so crawlers see them.
 */
export default function FooterColumn({
    item,
    defaultOpen = false,
}: {
    item: LayoutProps.MainFooterItem;
    /** Phones: start expanded (the most important column, so its links are seen) */
    defaultOpen?: boolean;
}) {
    const [open, setOpen] = useState(defaultOpen);
    // From the menu item, not useId(): useId depends on the component's place in the tree, which
    // now and then differed between server and browser and broke hydration in the footer.
    const listId = `footer-col-${item.databaseId}`;
    const label = cleanLabel(item.label);
    const Icon = iconFor(label);
    const links = item.childItems?.nodes || [];
    const href = item.uri && item.uri !== '#' ? item.uri : undefined;

    const icon = (
        <span className='flex size-8 shrink-0 items-center justify-center rounded-xl bg-white text-progress shadow-sm ring-1 ring-zinc-200/80 dark:bg-white/[0.06] dark:text-[#9FB0FF] dark:shadow-none dark:ring-white/10'>
            <Icon size={16} strokeWidth={1.8} aria-hidden='true' />
        </span>
    );

    return (
        <div className='border-b border-zinc-200 dark:border-white/10 lg:border-0'>
            {/* Phones: the row opens and closes the list */}
            <button
                type='button'
                aria-expanded={open}
                aria-controls={listId}
                onClick={() => setOpen((value) => !value)}
                className='flex w-full items-center gap-3 py-3 text-start lg:hidden'
            >
                {icon}
                <span className='flex-1 text-sm font-bold text-zinc-900 dark:text-white'>
                    {label}
                </span>
                <SpriteIcon
                    name='chevron-down'
                    size={18}
                    className={`shrink-0 text-zinc-400 transition-transform duration-300 dark:text-white/50 ${
                        open ? 'rotate-180' : ''
                    }`}
                />
            </button>

            {/* Desktop: the title, linked to its page when it has one */}
            <div className='hidden items-center gap-2.5 lg:flex'>
                {icon}
                {href ? (
                    <Link
                        prefetch={false}
                        href={href}
                        className='text-sm font-bold leading-6 text-zinc-900 transition-colors xl:text-[13px] hover:text-progress dark:text-white dark:hover:text-[#9FB0FF]'
                    >
                        {label}
                    </Link>
                ) : (
                    <p className='text-sm font-bold leading-6 text-zinc-900 dark:text-white xl:text-[13px]'>
                        {label}
                    </p>
                )}
            </div>

            <ul
                id={listId}
                className={`mb-3 ms-4 flex-col gap-0.5 border-s border-zinc-200 ps-4 dark:border-white/10 lg:mb-0 lg:ms-0 lg:mt-3 lg:flex lg:border-0 lg:ps-0 ${open ? 'flex' : 'hidden'}`}
            >
                {links.map((child, index) => (
                    <li key={`${child.uri}-${index}`}>
                        <Link
                            prefetch={false}
                            href={child.uri}
                            className='group flex items-center gap-1.5 py-1.5 text-[13px] leading-5 text-zinc-600 transition-colors hover:text-progress dark:text-white/60 dark:hover:text-white lg:gap-1 lg:py-1 lg:text-xs lg:text-zinc-500 lg:dark:text-white/55'
                        >
                            {/* Phones: a small marker at the start of each link (desktop shows one on hover at the end) */}
                            <SpriteIcon
                                name='chevron-left'
                                size={13}
                                className='shrink-0 text-zinc-400 dark:text-white/35 lg:hidden'
                            />
                            {cleanLabel(child.label)}
                            <SpriteIcon
                                name='chevron-left'
                                size={14}
                                className='shrink-0 opacity-0 transition-all group-hover:-translate-x-0.5 group-hover:opacity-100 max-lg:hidden'
                            />
                        </Link>
                    </li>
                ))}
                {/* The title link, for phones (on desktop the title itself is the link) */}
                {href && (
                    <li className='lg:hidden'>
                        <Link
                            prefetch={false}
                            href={href}
                            className='flex items-center gap-1.5 py-1.5 text-[13px] font-medium text-progress dark:text-[#9FB0FF]'
                        >
                            مشاهده همه
                            <SpriteIcon name='chevron-left' size={14} />
                        </Link>
                    </li>
                )}
            </ul>
        </div>
    );
}
